import endpoints from '../../../utils/commonVariables/endpoints';
import {
    readOnlyEntities,
    savableEntities,
} from '../../../utils/commonVariables/entityKeys';
import {
    supplierHotelContentViewModel,
    hotelContentViewModel,
} from '../utils/viewModels/childHotelContent';
import api, { cachedApi, localizedGet } from '../../../services/api';
import { buildOdataEndPoint } from '../../../utils/odataEndPoint';
import { IServiceType } from '../../../types/entities/IServiceType';
import { IApiResponse } from '../../../types/entities/entities';
import serviceType_attributeDefinitions from '../utils/viewModels/serviceType_AttributeDefinitions';
import {
    AvailableEditionLanguages,
    HotelContentState,
} from '../types/zitoneoServicesTypes';
import convertToOvariant from '../../../utils/convertToOvariant';
import { adaptStateHotelContentToDDBB } from './adapters/zitoneoAdapters';
import { CRITICAL_EXCEPTION_LEVEL_TYPE_KEY } from '../utils/zitoneoServicesUtils';

export const getSupplierHotelContentData = async (id: string) => {
    const endpoint = endpoints.viewModel(savableEntities.SupplierHotel, id);
    const res = await api.post(endpoint, supplierHotelContentViewModel);
    return res;
};

export const getServiceTypesData = async (lang: string) => {
    const endpoint = buildOdataEndPoint({
        entity: readOnlyEntities.ServiceType,
        select: 'Id,LocalizedCaption,Caption,Name,IdServiceCategory,DateCreated,ForRoomType,ForHotel',
        filter: [
            'Enabled',
            'ForZitoneo',
            'ServiceCategory/Enabled',
            'ServiceCategory/ForZitoneo',
        ],
        orderBy: 'LocalizedCaption asc',
    });
    const { data } = await localizedGet(endpoint, lang);
    return data;
};

export const getServiceCategoriesData = async (lang: string) => {
    const endpoint = buildOdataEndPoint({
        entity: readOnlyEntities.ServiceCategory,
        select: 'Id,LocalizedCaption,Caption,Name,Key,Description,LocalizedDescription,Icon,ForRoomType,ForHotel',
        filter: ['Enabled', 'ForZitoneo'],
        orderBy: 'LocalizedCaption asc',
    });
    const { data } = await localizedGet(endpoint, lang);
    return data;
};

export const getServiceAttributesData = async (lang: string) => {
    const endpoint = buildOdataEndPoint({
        entity: readOnlyEntities.AttributeDefinition,
        select: 'Id,Name,LocalizedName,Key',
        filter: ['Enabled'],
        orderBy: 'LocalizedName asc',
    });
    const { data } = await localizedGet(endpoint, lang);
    return data;
};

export const getLanguagesData = async () => {
    const cultureFilter = Object.values(AvailableEditionLanguages)
        .map(code => `CultureId eq '${code}'`)
        .join(' or ');
    const endpoint = buildOdataEndPoint({
        entity: readOnlyEntities.ContentLanguage,
        select: 'Id,Caption,LocalizedCaption,Name,CultureId',
        filter: ['Enabled', `(${cultureFilter})`],
    });
    const { data } = await cachedApi.get(endpoint);
    return data;
};

export const getHotelImageTypes = async (lang: string) => {
    const endpoint = buildOdataEndPoint({
        entity: readOnlyEntities.HotelImageType,
        select: 'Id,LocalizedCaption,Caption,Name,Key',
        filter: ['Enabled'],
        orderBy: 'LocalizedCaption asc',
    });
    const { data } = await localizedGet(endpoint, lang);
    return data;
};

export const getServiceAttributeDefinitions = async (idServiceType: number) => {
    const endpoint = endpoints.viewModel(
        readOnlyEntities.ServiceType,
        idServiceType
    );
    return await api.post<IApiResponse<IServiceType>>(
        endpoint,
        serviceType_attributeDefinitions
    );
};

export const saveZitoneoHotelContent = (
    hotelContent: Partial<HotelContentState>,
    idSysUser: number
) => {
    const data = adaptStateHotelContentToDDBB(hotelContent, idSysUser);

    const objectBase64 = convertToOvariant(
        savableEntities.Hotel,
        data,
        hotelContent.IdHotel,
        hotelContentViewModel
    );

    return api.post(endpoints.commonSave, objectBase64);
};

export const uploadZitoneoLogoImage = (file: File) => {
    const endpoint = endpoints.uploadFilesToHotel;
    const filesToUpload = new FormData();
    filesToUpload.append('', file);

    return api.post(endpoint, filesToUpload);
};

export const exportZitoneoHotelContentToExcel = (idSupplierHotel: string) => {
    const endpoint = endpoints.zitoneoExportExcel(idSupplierHotel);
    return api.get(endpoint);
};

export const getZitoneoPublishedStatusTypesData = async () => {
    const endpoint = buildOdataEndPoint({
        entity: readOnlyEntities.ZitoneoPublishStatusType,
        select: 'Id,LocalizedCaption,Caption,Name,Key',
        filter: ['Enabled'],
        orderBy: 'LocalizedCaption asc',
    });
    const { data } = await api.get(endpoint);
    return data;
};

export const getServiceExceptionLevelTypeData = async () => {
    const endpoint = buildOdataEndPoint({
        entity: readOnlyEntities.ServiceExceptionLevelType,
        select: 'Id,LocalizedCaption,Caption,Name,Key',
        filter: [`Key eq '${CRITICAL_EXCEPTION_LEVEL_TYPE_KEY}'`],
        orderBy: 'LocalizedCaption asc',
    });
    const { data } = await api.get(endpoint);
    return data;
};

export const changeStatus = (
    idHotel: number,
    statusKey: string,
    SystemUserName: string
) => {
    const endpoint = endpoints.zitoneoChangeStatus;
    const data = {
        Id: idHotel,
        NewStatus: statusKey,
        Message: `Status changed by ${SystemUserName} on ${new Date().toLocaleString()} via Owner Portal`,
    };
    return api.post(endpoint, data);
};

export const getUsedZitoneoURLs = async (idHotel: number) => {
    const endpoint = buildOdataEndPoint({
        entity: readOnlyEntities.ZitoneoHotelUrl,
        select: 'Id,IdHotel,Path,Enabled,IsPrimary',
        filter: [`IdHotel eq ${idHotel}`],
    });
    const { data } = await api.get(endpoint);
    return data;
};
